import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  cancelBulkJob,
  fetchAssignees,
  fetchBulkJobs,
  pauseBulkJob,
  resumeBulkJob,
} from '../services/taskApi';
import { formatTaskDueAt } from '../services/taskTimezoneUtils';

const STATUS_LABELS = {
  queued: 'Queued',
  processing: 'Processing',
  paused: 'Paused',
  complete: 'Complete',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

export function TaskBulkActionsPage({ workspace }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [userId, setUserId] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBulkJobs(workspace, {
        status: status || undefined,
        requested_by_user_id: userId || undefined,
        created_from: createdFrom ? new Date(`${createdFrom}T00:00:00Z`).toISOString() : undefined,
        created_to: createdTo ? new Date(`${createdTo}T23:59:59Z`).toISOString() : undefined,
        page,
        page_size: 20,
      });
      setItems(data.items || []);
      setTotalPages(Math.max(1, Math.ceil((data.total_items || 0) / 20)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load bulk actions');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [workspace, status, userId, createdFrom, createdTo, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchAssignees(workspace)
      .then((data) => setUsers(data.items || []))
      .catch(() => setUsers([]));
  }, [workspace]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground" data-testid="task-bulk-actions-page">
      <div className="border-b border-border px-4 py-4 sm:px-6">
        <Button variant="ghost" className="mb-2 px-0" onClick={() => navigate(workspace.routes.tasks)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold">Bulk actions - Tasks</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Review queued, processing, and completed bulk task operations for this workspace.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3 sm:px-6">
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Created from</p>
          <Input type="date" value={createdFrom} onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }} />
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Created to</p>
          <Input type="date" value={createdTo} onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }} />
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Status</p>
          <Select value={status || '__all__'} onValueChange={(v) => { setStatus(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[160px]"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">User</p>
          <Select value={userId || '__all__'} onValueChange={(v) => { setUserId(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Any user" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any user</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.userId} value={u.userId}>{u.displayName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-2 text-center" data-testid="bulk-actions-empty">
            <h2 className="text-lg font-semibold">No bulk actions yet</h2>
            <p className="text-sm text-muted-foreground">Bulk operations you run on tasks will appear here.</p>
          </div>
        ) : (
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-muted/60 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Action label</th>
                <th className="px-4 py-3">Operation</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Completed</th>
                <th className="px-4 py-3">Statistics</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((job) => {
                const stats = job.statistics || {};
                const user = users.find((u) => u.userId === job.requestedByUserId);
                return (
                  <tr key={job.bulkJobId} className="border-b border-border">
                    <td className="px-4 py-3">{job.actionLabel}</td>
                    <td className="px-4 py-3">{job.operation}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{STATUS_LABELS[job.status] || job.status}</Badge>
                    </td>
                    <td className="px-4 py-3">{user?.displayName || job.requestedByUserId}</td>
                    <td className="px-4 py-3">{formatTaskDueAt(job.createdAt, workspace.timezone)}</td>
                    <td className="px-4 py-3">{formatTaskDueAt(job.completedAt, workspace.timezone)}</td>
                    <td className="px-4 py-3 text-xs">
                      {stats.success}/{stats.total} ok · {stats.failed} failed · {stats.processed} processed
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" aria-label="Bulk job actions">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {['queued', 'processing'].includes(job.status) ? (
                            <DropdownMenuItem onClick={async () => { await pauseBulkJob(workspace, job.bulkJobId); load(); }}>
                              Pause
                            </DropdownMenuItem>
                          ) : null}
                          {job.status === 'paused' ? (
                            <DropdownMenuItem onClick={async () => { await resumeBulkJob(workspace, job.bulkJobId); load(); }}>
                              Resume
                            </DropdownMenuItem>
                          ) : null}
                          {['queued', 'processing', 'paused'].includes(job.status) ? (
                            <DropdownMenuItem onClick={async () => { await cancelBulkJob(workspace, job.bulkJobId); load(); }}>
                              Cancel
                            </DropdownMenuItem>
                          ) : null}
                          {job.exportCsv ? (
                            <DropdownMenuItem
                              onClick={() => {
                                const blob = new Blob([job.exportCsv], { type: 'text/csv' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${job.bulkJobId}.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                            >
                              Download export
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-border px-4 py-3 sm:px-6">
        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}

export default TaskBulkActionsPage;
